/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { ParameterForm } from '@karya/parameter-specs';
import { FormContext } from '../hooks/useForm';
import { DefinitionRendererConfig } from './ParameterDefinitionRenderer';
import { SectionRendererBuilder } from './SectionRenderer';

export interface FormRendererConfig {
  components: DefinitionRendererConfig;
}

export interface FormRendererProps<T> {
  ctx: FormContext<T>;
  parameters?: ParameterForm<T>;
  sectionless?: boolean;
}

export function FormRendererBuilder(config: FormRendererConfig) {
  const { FormContainer = React.Fragment } = config.components;
  const SectionRenderer = SectionRendererBuilder(config);

  return React.memo<FormRendererProps<any>>((props) => {
    const ctx = props.ctx;
    let parameters = props.parameters;
    if (!parameters) {
      parameters = ctx.parameters;
    }
    const isSectionless = props.sectionless ?? props.ctx.sectionless;
    const formBody = parameters.map((section, index) => (
      <SectionRenderer
        ctx={props.ctx}
        section={section}
        key={index}
        index={isSectionless ? undefined : index}
        isLast={isSectionless ? undefined : index === parameters.length - 1}
        showStepper={!isSectionless}
      />
    ));

    if (isSectionless) {
      return <React.Fragment>{formBody}</React.Fragment>;
    }

    return <FormContainer>{formBody}</FormContainer>;
  });
}
